import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Users, UserCog, Settings as SettingsIcon, Globe, Crown } from "lucide-react";

export default function AdminHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ teams: null, users: null, club: null });

  useEffect(() => {
    if (user && user.role !== "club_admin") {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const [t, u, c] = await Promise.all([
          api.get("/clubs/me/teams", { params: { include_inactive: true } }),
          api.get("/clubs/me/users"),
          api.get("/clubs/me"),
        ]);
        setStats({
          teams: t.data.length,
          activeTeams: t.data.filter((x) => x.active).length,
          users: u.data.length,
          activeUsers: u.data.filter((x) => x.active).length,
          club: c.data,
        });
      } catch { /* ignore */ }
    })();
  }, []);

  if (user?.role && user.role !== "club_admin") return null;

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-4xl mx-auto" data-testid="admin-home-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-2">Verwaltung</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase">
          {stats.club?.name || "Verein"}
        </h1>
        <p className="text-zinc-400 text-sm mt-2">
          Verwalte Mannschaften und Trainer deines Vereins.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-tiles">
        <Link to="/admin/teams"
          className="group rounded-lg border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 hover:border-brand-lighter/50 transition p-5"
          data-testid="admin-tile-teams">
          <div className="flex items-start justify-between">
            <div className="h-11 w-11 bg-brand/20 group-hover:bg-brand/30 transition flex items-center justify-center rounded-md">
              <Users className="h-6 w-6 text-brand-lighter" />
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-bold text-zinc-100 tabular-nums">
                {stats.activeTeams ?? "—"}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                {stats.teams && stats.teams !== stats.activeTeams
                  ? `${stats.teams} gesamt`
                  : "Mannschaften"}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="font-semibold text-zinc-100">Mannschaften</div>
            <div className="text-xs text-zinc-500 mt-0.5">Anlegen, umbenennen, deaktivieren</div>
          </div>
        </Link>

        <Link to="/admin/users"
          className="group rounded-lg border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 hover:border-brand-lighter/50 transition p-5"
          data-testid="admin-tile-users">
          <div className="flex items-start justify-between">
            <div className="h-11 w-11 bg-brand/20 group-hover:bg-brand/30 transition flex items-center justify-center rounded-md">
              <UserCog className="h-6 w-6 text-brand-lighter" />
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-bold text-zinc-100 tabular-nums">
                {stats.activeUsers ?? "—"}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                {stats.users && stats.users !== stats.activeUsers
                  ? `${stats.users} gesamt`
                  : "Trainer"}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="font-semibold text-zinc-100">Benutzer</div>
            <div className="text-xs text-zinc-500 mt-0.5">Trainer anlegen, Rollen, Zuweisungen</div>
          </div>
        </Link>

        {user?.is_super_admin && (
          <Link to="/super"
            className="group rounded-lg border border-amber-900/40 bg-amber-900/10 hover:bg-amber-900/20 hover:border-amber-500/50 transition p-5 sm:col-span-2"
            data-testid="admin-tile-super">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 bg-amber-500/20 group-hover:bg-amber-500/30 transition flex items-center justify-center rounded-md">
                <Crown className="h-6 w-6 text-amber-400" />
              </div>
              <div className="text-[10px] uppercase tracking-wider text-amber-400/80">
                Super-Admin
              </div>
            </div>
            <div className="mt-4">
              <div className="font-semibold text-zinc-100">Plattform-Verwaltung</div>
              <div className="text-xs text-zinc-500 mt-0.5">Globaler Zugriff auf alle Vereine, Mannschaften und Nutzer</div>
            </div>
          </Link>
        )}

        {(user?.email || "").toLowerCase() === "trainer@demo.de" && (
          <Link to="/admin/landing"
            className="group rounded-lg border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 hover:border-brand-lighter/50 transition p-5 sm:col-span-2"
            data-testid="admin-tile-landing">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 bg-amber-500/20 group-hover:bg-amber-500/30 transition flex items-center justify-center rounded-md">
                <Globe className="h-6 w-6 text-amber-400" />
              </div>
              <div className="text-[10px] uppercase tracking-wider text-amber-400/80">
                Owner only
              </div>
            </div>
            <div className="mt-4">
              <div className="font-semibold text-zinc-100">Landingpage CMS</div>
              <div className="text-xs text-zinc-500 mt-0.5">Inhalte der öffentlichen Startseite pflegen</div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
